import { alphaColor, colors } from '_/space-management/constants/theme';
import { networkErrorMessage } from '_/space-management/helpers/networkError';
import { eventsApi } from '_/space-management/services/api';
import { ReqBase } from '_/space-management/services/api/types/ReqQuery';

import { ErrorCode } from '_/space-management/services/models/enums/error-code.enum';
import { EventStatus } from '_/space-management/services/models/enums/event-status.enum';
import { getSpaceTypeColor } from '_/space-management/services/models/enums/space-type.enum';
import { MembersModel } from '_/space-management/services/models/members.model';
import { SlotsModel } from '_/space-management/services/models/slots.model';
import { endOfDay, startOfDay } from 'date-fns';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { RRule, Frequency } from 'rrule';

import {
  CreateReservationType,
  EventContextData,
  EventsResponse,
  EventUpdateType,
  WeekDaysTypes,
  GetAvailableSlotsType,
} from '_/space-management/services/models/events.model';
import { Role } from '_/space-management/services/models/enums/role.enum';
import { useAuth } from './AuthContext';
import { useDateContext } from './DateContext';
import { useSlotContext } from './SlotContext';
import { useSpaceContext } from './SpaceContext';
import { appConfig } from '_/config/app';
import { showMessage } from 'react-native-flash-message';

const EventContext = createContext<EventContextData>({} as EventContextData);

type EventType = {
  children?: React.ReactNode;
};

export const EventProvider: React.FC<EventType> = ({ children }) => {
  const { member } = useAuth();
  const { spaceType } = useSpaceContext();
  const modalMessage = useRef<any>(null);
  const { t } = useTranslation();

  const [activeReservations, setActiveReservations] = useState<EventsResponse[]>([]);
  const [pastReservations, setPastReservations] = useState<EventsResponse[]>([]);
  const [meetingReservations, setMeetingReservations] = useState<EventsResponse[]>();
  const [hasActiveReservations, setHasActiveReservations] = useState(false);
  const [hasPastReservations, setHasPastReservations] = useState(false);
  const [byWeekDay, setByWeekDay] = useState<Partial<WeekDaysTypes[]>>();
  const [recurrenceInterval, setRecurrenceInterval] = useState<string | undefined>('1');
  const [freq, setFreq] = useState<Frequency>(RRule.WEEKLY);
  const [totalCurrentEvents, setTotalCurrentEvents] = useState<number>(0);
  const [recurrenceStr, setRecurrenceStr] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [reservedListLoading, setReservedListLoading] = useState(false);
  const [availableListLoading, setAvailableListLoading] = useState<
    'endReached' | 'loadData' | null
  >(null);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [eventsConflicts, setEventsConflicts] = useState<string[]>([]);
  const [closedDates, setClosedDates] = useState<string[]>([]);
  const [userEventsConflicts, setUserEventsConflicts] = useState<string[]>([]);
  const [activeReservationsResponseTotal, setActiveReservationsResponseTotal] = useState(0);
  const [pastReservationsResponseTotal, setPastReservationsResponseTotal] = useState(0);
  const [todayReservations, setTodayReservations] = useState<EventsResponse[]>([]);

  const { currentSpace } = useSpaceContext();
  const { selectedEventDate } = useDateContext();

  const {
    setAvailableListPage,
    setAvailableList,
    availableListPage,
    setReservedList,
    setSlotsList,
    setAvailableListEndReached,
  } = useSlotContext();

  const createReservationEvent = async ({
    memberId,
    slotId,
    startAtTime,
    endAtTime,
    recurrenceStr,
    dropdownField1OptionId,
    dropdownField2OptionId,
    textField1Value,
    textField2Value,
    description,
    createEvent,
    costCenter,
  }: CreateReservationType) => {
    setLoading(true);

    if (createEvent !== true) {
      setCalendarLoading(true);
    }

    try {
      const result = await eventsApi.insert({
        createEvent,
        createdByMemberId: member?.id,
        createdToMemberId: memberId,
        slotId,
        startAt: startAtTime,
        endAt: endAtTime,
        costCenter,
        ...(description && { description }),
        ...(dropdownField1OptionId && { dropdownField1OptionId }),
        ...(dropdownField1OptionId && { dropdownField2OptionId }),
        ...(textField1Value && { textField1Value }),
        ...(textField2Value && { textField2Value }),
        ...(recurrenceStr && { recurrenceStr }),
      });

      const { conflicts } = result as { conflicts?: Date[] };

      setAvailableDates(result.availableDates || []);
      setEventsConflicts(result.eventsConflicts || []);
      setUserEventsConflicts(result.userEventsConflicts || []);
      setClosedDates(result.disabledDatesConflicts || []);

      setCalendarLoading(false);

      if (conflicts && conflicts?.length > 0) {
        showMessage({
          message: t('spaceManage.success'),
          description: t(
            'spaceManage.workstationScreen.successMessages.reservationButWithConflict'
          ),
          backgroundColor: colors.successGreen,
        });
      }

      if (createEvent) {
        showMessage({
          message: t('spaceManage.success'),
          description: t('spaceManage.workstationScreen.successMessages.reservationCreated'),
          backgroundColor: colors.successGreen,
        });
      }
    } catch (error) {
      setLoading(false);
      networkErrorMessage(error);
      throw error;
    } finally {
      setLoading(false);
      if (createEvent !== true) {
        setCalendarLoading(false);
      }
    }
  };

  const getEventsbyMemberId = async (member: Partial<MembersModel>) => {
    return await eventsApi.getList({
      createdToMemberId: member.id,
    });
  };

  async function getActiveEvents(skip = 0, endAt?: Date | null, isloadMore?: boolean) {
    setEventsLoading(true);

    const params = {
      'status[$nin]': [EventStatus.CANCELED, EventStatus.CHECKEDOUT],
      createdToMemberId: member?.id,
      $limit: appConfig.spaceManagement.apiLimit,
      $skip: skip,
      '$sort[startAt]': 1,
      'startAt[$gte]': startOfDay(new Date()),
      ...(endAt && { 'endAt[$lte]': endAt }),
    };

    const { data, total } = await eventsApi.getList(params);

    if (isloadMore) {
      setActiveReservations([...activeReservations, ...data]);
    } else {
      setActiveReservations(data);
    }

    setActiveReservationsResponseTotal(total as number);
    setEventsLoading(false);

    return data;
  }

  async function getPastEvents(skip = 0, isloadMore?: boolean) {
    setEventsLoading(true);

    const params = {
      'status[$ne]': EventStatus.CANCELED,
      createdToMemberId: member?.id,
      $limit: appConfig.spaceManagement.apiLimit,
      $skip: skip,
      '$sort[startAt]': -1,
      '$or[0][status]': EventStatus.CHECKEDOUT,
      '$or[1][startAt][$lt]': startOfDay(new Date()),
    };

    const { data, total } = await eventsApi.getList(params);
    setPastReservationsResponseTotal(total as number);

    if (isloadMore) {
      setPastReservations([...pastReservations, ...data]);
    } else {
      setPastReservations(data);
    }

    setEventsLoading(false);

    return data;
  }

  async function getTodayEvents() {
    const params = {
      'status[$nin]': [EventStatus.CANCELED, EventStatus.CHECKEDOUT],
      createdToMemberId: member?.id,
      $limit: appConfig.spaceManagement.apiLimit,
      '$sort[startAt]': 1,
      'startAt[$gte]': startOfDay(new Date()),
      'endAt[$lte]': endOfDay(new Date()),
    };

    const { data } = await eventsApi.getList(params);

    const checkoutFilter = data.filter((item) => item.checkoutAt === null);

    setTodayReservations(checkoutFilter);
  }

  const getEvents = useCallback(async () => {
    const activeEvents = await getActiveEvents();

    await getTodayEvents();

    return activeEvents;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member]);

  const getMeetings = useCallback(
    async ({ slotId, startAt, endAt }: { slotId: string; startAt: Date; endAt: Date }) => {
      const result = await eventsApi.getList({
        slotId,
        'startAt[$gte]': startAt,
        'endAt[$lte]': endAt,
        'status[$ne]': EventStatus.CANCELED,
      });

      return result.data;
    },
    []
  );

  const cancelEvent = async (id: string) => {
    setLoading(true);
    try {
      const result = await eventsApi.update(id, {
        status: EventStatus.CANCELED,
      });

      await getActiveEvents();
      await getTodayEvents();
      showMessage({
        message: t('spaceManage.success'),
        description: t('spaceManage.workstationScreen.successMessages.cancelledReservation'),
        backgroundColor: colors.successGreen,
      });
      return result;
    } catch (error) {
      setLoading(false);
      networkErrorMessage(error);
      if (error?.response?.data?.data?.code === ErrorCode.ONLY_ADMIN) {
        showMessage({
          message: t('spaceManage.error'),
          description: t('spaceManage.workstationScreen.errorMessages.onlyAdmin'),
          backgroundColor: colors.errorRed,
        });
      } else {
        showMessage({
          message: t('spaceManage.error'),
          description: t('spaceManage.workstationScreen.errorMessages.cancelFail'),
          backgroundColor: colors.errorRed,
        });
      }

      throw error;
    } finally {
      setLoading(false);
    }
  };

  const cancelRecurrenceEvent = async (recurrenceId?: string) => {
    setLoading(true);
    try {
      await eventsApi.update(
        '',
        { status: EventStatus.CANCELED },
        {
          params: { recurrenceId },
        }
      );

      await getActiveEvents();
      await getTodayEvents();
      showMessage({
        message: t('spaceManage.success'),
        description: t('spaceManage.workstationScreen.successMessages.cancelledRecurring'),
        backgroundColor: colors.successGreen,
      });
    } catch (error) {
      setLoading(false);
      networkErrorMessage(error);
      if (error?.response?.data?.data?.code === ErrorCode.ONLY_ADMIN) {
        showMessage({
          message: t('spaceManage.error'),
          description: t('spaceManage.workstationScreen.errorMessages.onlyAdmin'),
          backgroundColor: colors.errorRed,
        });
      } else {
        showMessage({
          message: t('spaceManage.error'),
          description: t('spaceManage.workstationScreen.errorMessages.cancelRecurringFail'),
          backgroundColor: colors.errorRed,
        });
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getSpaceEvents = useCallback(
    async ({ spaceId, startAtTime, endAtTime, ...rest }: GetAvailableSlotsType & ReqBase<any>) => {
      return await eventsApi.getSpaceEvents({ spaceId, startAtTime, endAtTime, ...rest });
    },
    []
  );

  const getSlotAvailability = useCallback(
    async (slotId: string, start: Date, end: Date, includeSchedules?: boolean) => {
      return await eventsApi.getSlotAvailability(slotId, start, end, includeSchedules);
    },
    []
  );

  const getAvailableList = async ({
    limit,
    reset,
    startAtTime,
    endAtTime,
    spaceId,
    slotName,
  }: GetAvailableSlotsType) => {
    if (availableListLoading) {
      return;
    }

    const page = reset ? 0 : availableListPage + 1;

    if (reset && availableListLoading === null) {
      setAvailableListLoading('loadData');
    } else if (!reset && availableListLoading === null) {
      setAvailableListLoading('endReached');
    }

    setAvailableListPage(page);

    let queryStart = startAtTime;
    let queryEnd = endAtTime;

    if (currentSpace?.scheduleEnabled) {
      queryStart = startAtTime ? startOfDay(startAtTime) : startAtTime;
      queryEnd = endAtTime ? endOfDay(endAtTime) : endAtTime;
    }

    const data = await getSpaceEvents({
      spaceId,
      startAtTime: queryStart,
      endAtTime: queryEnd,
      $limit: limit,
      $skip: page * (limit || 0),
      ...(slotName && { slotName }),
    });

    setAvailableListEndReached(!!limit && data?.slots.length < limit);
    setSlotsList(data.slots);
    setTotalCurrentEvents(data.currentCapacity);

    // const slotsWithNoEvents = data.slots.filter((slot: SlotsModel) => slot.isAvailable);

    if (reset) {
      setAvailableList(data.slots);
    } else {
      setAvailableList((prevSlots) =>
        prevSlots.concat(data.slots.filter((slot: any) => prevSlots.indexOf(slot) === -1))
      );
    }
    setAvailableListLoading(null);
  };

  const getReservedList = useCallback(
    async ({
      spaceId,
      startAtTime,
      endAtTime,
    }: {
      spaceId: string;
      startAtTime?: Date;
      endAtTime?: Date;
    }) => {
      setReservedListLoading(true);
      try {
        const result = await eventsApi.getSpaceReservations(
          spaceId,
          startAtTime || startOfDay(new Date()),
          endAtTime || endOfDay(new Date())
        );
        setReservedList(result);
        setReservedListLoading(false);
      } catch (err) {
        setReservedListLoading(false);
        showMessage({
          message: t('informationIsNotAvailable'),
          type: 'danger'
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const setReservationColor = useCallback(
    (parentEventId: string | undefined) => {
      if (parentEventId) {
        return alphaColor(getSpaceTypeColor(spaceType), 0.7);
      }
      return getSpaceTypeColor(spaceType);
    },
    [spaceType]
  );

  const meetingEvents = useMemo(() => {
    return meetingReservations?.map((reservation) => {
      const isReserver = reservation.createdToMemberId === member?.id || reservation.user?.id === member?.userId;
      const canSeeDetails = member?.role === Role.ADMIN || isReserver;

      return {
        id: reservation.id,
        description: reservation.description,
        startDate: reservation.checkinAt || reservation.startAt,
        endDate: reservation.checkoutAt || reservation.endAt,
        color: setReservationColor(reservation.parentEventId),
        source: canSeeDetails ? reservation.user?.pictureThumbUrl : undefined,
        name: canSeeDetails 
          ? (reservation.user?.name || (reservation.parentEventId && t('spaceManage.reportsScreen.CLEANING')))
          : t('spaceManage.reserved'),
        slotId: reservation.slotId,
        parentEventId: reservation.parentEventId,
      };
    });
  }, [meetingReservations, setReservationColor, member, t]);

  const maxCapacityReached = useMemo(() => {
    if (currentSpace.maxCapacity) {
      return totalCurrentEvents >= currentSpace.maxCapacity;
    }
    return false;
  }, [currentSpace, totalCurrentEvents]);

  const updateEvent = useCallback(async (id: string, eventData: EventUpdateType) => {
    return await eventsApi.update(id, {
      startAt: eventData.startAt,
      endAt: eventData.endAt,
      description: eventData.description,
      dropdownField1OptionId: eventData.dropdownField1OptionId,
      dropdownField2OptionId: eventData.dropdownField2OptionId,
      textField1Value: eventData.textField1Value,
      textField2Value: eventData.textField2Value,
      costCenter: eventData.costCenter,
    });
  }, []);

  useEffect(() => {
    setTotalCurrentEvents(0);
  }, [selectedEventDate]);

  return (
    <EventContext.Provider
      value={{
        createReservationEvent,
        getEventsbyMemberId,
        getEvents,
        activeReservations,
        pastReservations,
        hasActiveReservations,
        setHasActiveReservations,
        setHasPastReservations,
        hasPastReservations,
        cancelEvent,
        setByWeekDay,
        byWeekDay,
        recurrenceInterval,
        setRecurrenceInterval,
        setFreq,
        freq,
        recurrenceStr,
        setRecurrenceStr,
        cancelRecurrenceEvent,
        getSpaceEvents,
        getSlotAvailability,
        loading,
        setLoading,
        calendarLoading,
        setCalendarLoading,
        getMeetings,
        getAvailableList,
        getReservedList,
        availableListLoading,
        modalMessage,
        meetingReservations,
        setMeetingReservations,
        meetingEvents,
        availableDates,
        eventsConflicts,
        userEventsConflicts,
        reservedListLoading,
        closedDates,
        maxCapacityReached,
        updateEvent,
        getActiveEvents,
        getPastEvents,
        activeReservationsResponseTotal,
        pastReservationsResponseTotal,
        getTodayEvents,
        todayReservations,
        eventsLoading,
      }}
    >
      {children}
    </EventContext.Provider>
  );
};

export function useEventContext(): EventContextData {
  const context = useContext(EventContext);

  if (!context) {
    throw new Error('useEvent must be used within an EventProvider');
  }

  return context;
}
